import { useEffect } from "react";
import get from "lodash/get";
import {
  getMonth,
  getDate,
  getYear,
  getHours,
  getMinutes,
  differenceInHours,
  differenceInMinutes,
  differenceInSeconds,
  format
} from "date-fns";

import {
  BREAK_POINT_MIN,
  BREAK_POINT_TABLET,
  MEDIA_TABLET,
  BREAK_POINT_MAX,
  ENG_GOOGLE_PLAYSTORE,
  ENG_APPLE_PLAYSTORE,
  BN_APPLE_PLAYSTORE,
  BN_GOOGLE_PLAYSTORE
} from "./constant";
import { parseUrl } from "query-string";

export const getSlug = (collection, sections = [], domainSlug) => {
  let slug = "#";

  if (collection && collection.slug) {
    const metadata = Object.keys(get(collection, ["metadata"], {}));
    const isSection = metadata.indexOf("section") !== -1;

    if (sections && sections.length > 0 && isSection) {
      const sectionId = collection.metadata.section[0].id;
      const section = sections.find(item => item.id === sectionId);

      if (section && section["parent-id"] && !domainSlug) {
        const parentSection = sections.find(item => item.id === section["parent-id"]);

        if (parentSection && !domainSlug) {
          slug = `/${parentSection.slug}/${section.slug}`;
        }
      } else if ((section && !section["parent-id"]) || (section && domainSlug)) {
        slug = `/${section.slug}`;
      }
    } else {
      slug = `/collection/${collection.slug}`;
    }
  }

  return slug;
};

export const getStoryHeadline = story => {
  return get(story, ["alternative", "home", "default", "headline"]) || story.headline;
};

export const getStoryData = (story, isAlternativeImageFirst = true) => {
  let storyData = {
    imageS3Key: null
  };

  if (!story) {
    return storyData;
  }

  if (isAlternativeImageFirst) {
    storyData = getAlternativeData(story);
    if (!storyData.imageS3Key) {
      storyData = getMainData(story);
    }
  } else {
    storyData = getMainData(story);
    if (!storyData.imageS3Key) {
      storyData = getAlternativeData(story);
    }
  }
  return storyData;
};

const getAlternativeData = story => {
  const storyData = {};
  storyData.headline = getStoryHeadline(story);
  storyData.imageS3Key = get(story, ["alternative", "home", "default", "hero-image", "hero-image-s3-key"], null);
  storyData.imageCaption =
    get(story, ["alternative", "home", "default", "hero-image", "hero-image-caption"]) || storyData.headline;

  storyData.imageAttribution = get(
    story,
    ["alternative", "home", "default", "hero-image", "hero-image-attribution"],
    null
  );

  storyData.imageMetadata = get(story, ["alternative", "home", "default", "hero-image", "hero-image-metadata"], null);
  return storyData;
};

const getMainData = story => {
  const storyData = {};
  storyData.headline = getStoryHeadline(story);
  storyData.subHeadline = story.subheadline || null;
  storyData.imageS3Key = get(story, ["hero-image-s3-key"], null);
  storyData.imageCaption = get(story, ["hero-image-caption"]) || storyData.headline;
  storyData.imageMetadata = story["hero-image-metadata"];
  storyData.imageAttribution = get(story, ["hero-image-attribution"], null);
  return storyData;
};

export const generateImageSources = (
  mobile = { aspectRatio: [1, 1], screenWidthCoverage: 1, srcSetCount: 3 },
  desktop = { aspectRatio: [1, 1], screenWidthCoverage: 1, srcSetCount: 3 }
) => {
  return [
    Object.assign(
      {},
      generateImageSource(desktop.screenWidthCoverage, BREAK_POINT_TABLET, BREAK_POINT_MAX, desktop.srcSetCount),
      {
        aspectRatio: desktop.aspectRatio,
        media: MEDIA_TABLET
      }
    ),
    Object.assign(
      {},
      generateImageSource(mobile.screenWidthCoverage, BREAK_POINT_MIN, BREAK_POINT_TABLET, mobile.srcSetCount),
      {
        aspectRatio: mobile.aspectRatio
      }
    )
  ];
};

const generateImageSource = (
  screenWidthCoverage = 1,
  min = BREAK_POINT_MIN,
  max = BREAK_POINT_MAX,
  srcSetCount = 3
) => {
  const minImageSize = min * screenWidthCoverage;
  const maxImageSize = max * screenWidthCoverage;
  const srcSetDiff = (maxImageSize - minImageSize) / srcSetCount;

  const widths = [];
  for (let i = 1; i <= srcSetCount; i++) {
    widths.push(parseInt(minImageSize + srcSetDiff * i));
  }

  const sizes =
    "(max-width: " + max + "px) " + parseInt(screenWidthCoverage * 100) + "vw, " + widths[widths.length - 1] + "px";

  const source = {
    defaultWidth: widths[Math.floor(widths.length / 2)],
    widths: widths,
    sizes: sizes
  };
  return source;
};

/**
 * Instantiates an image element for Lightbox
 *
 * @param object story
 * @returns object
 */
export const createImageElement = story => {
  return Object.assign(
    {},
    {
      id: story.id,
      "image-metadata": story["hero-image-metadata"],
      "image-s3-key": story["hero-image-s3-key"],
      type: "image"
    }
  );
};

/**
 * Validate the Email
 */
export function isValidEmail(email) {
  // eslint-disable-next-line max-len
  const re = /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i;
  if (!re.test(email)) return false;
  if (email.length > 150 || email.length < 6) return false;
  return true;
}

/**
 * Get the date for the header in Bangla
 */
export const getDateFromUtils = () => {
  const currentDate = new Date();
  const options = { weekday: "long", year: "numeric", month: "long", day: "numeric" };
  const banglaDate = currentDate.toLocaleString("bn-BN", options);

  return banglaDate;
};

export const getDeviceType = () => {
  if (global.deviceWidth < 876) {
    return "mobile";
  } else if (global.deviceWidth > 875 && global.deviceWidth < 1264) {
    return "tablet";
  }

  return "desktop";
};

/**
 * Given english number returns a bangla number
 */
export const toBanglaNum = value => {
  const numbersObj = {
    0: "০",
    1: "১",
    2: "২",
    3: "৩",
    4: "৪",
    5: "৫",
    6: "৬",
    7: "৭",
    8: "৮",
    9: "৯",
    ".": ".",
    "-": "-"
  };

  if (isNaN(parseFloat(value)) || isNaN(value - 0)) {
    return "Invalid input type";
  }

  let str = "";

  return (
    value
      .toString()
      .split("")
      .forEach(num => (str += numbersObj[num])),
    str
  );
};

export const toBanglaMonth = value => {
  const monthsObj = {
    1: "জানুয়ারী",
    2: "ফেব্রুয়ারি",
    3: "মার্চ",
    4: "এপ্রিল",
    5: "সোব",
    6: "জুন",
    7: "জুলাই",
    8: "অগাস্ট",
    9: "সেপ্টেম্বর",
    10: "অক্টোবর",
    11: "নভেম্বর",
    12: "ডিসেম্বর"
  };

  return monthsObj[value];
};

export const toEngMonth = value => {
  const monthsObj = {
    1: "January",
    2: "February",
    3: "March",
    4: "April",
    5: "May",
    6: "June",
    7: "July",
    8: "August",
    9: "September",
    10: "October",
    11: "November",
    12: "December"
  };

  return monthsObj[value];
};

export const getEngStoryPageDate = date => {
  const hourDifference = differenceInHours(new Date(), new Date(date));
  const minuteDifference = differenceInMinutes(new Date(), new Date(date));
  const secondsDifference = differenceInSeconds(new Date(), new Date(date));
  const day = getDate(date);
  const year = getYear(date);
  const hours = getHours(date);

  if (secondsDifference < 60) {
    return "Few moments ago";
  } else if (secondsDifference >= 60 && secondsDifference < 119) {
    return "1 minute ago";
  } else if (secondsDifference >= 120 && minuteDifference < 59) {
    return `${minuteDifference} minutes ago`;
  } else if (minuteDifference >= 60 && hourDifference < 2) {
    return "1 hour ago";
  } else if (hourDifference >= 2 && hourDifference < 24) {
    return `${hourDifference} hours ago`;
  } else return `${day} ${format(new Date(date), "MMMM")} ${year}, ${hours}:${format(new Date(date), "mm")}`;
};

export const getStoryPageDate = (publishedTime, lang) => {
  if (lang === "en") {
    return getEngStoryPageDate(publishedTime);
  }

  const monthname = lang === "bn" ? toBanglaMonth(getMonth(publishedTime) + 1) : getMonth(publishedTime) + 1;
  const date = lang === "bn" ? toBanglaNum(getDate(publishedTime)) : getDate(publishedTime);
  const year = lang === "bn" ? toBanglaNum(getYear(publishedTime)) : getYear(publishedTime);
  const hours = lang === "bn" ? toBanglaNum(getHours(publishedTime)) : getHours(publishedTime);
  const minutes =
    lang === "bn"
      ? toBanglaNum(getMinutes(publishedTime) < 10 ? "0" + getMinutes(publishedTime) : getMinutes(publishedTime))
      : getMinutes(publishedTime) < 10
      ? "0" + getMinutes(publishedTime)
      : getMinutes(publishedTime);

  // following lines keep commented for future use, if it needs 12hr format
  // const getHour = getHours(publishedTime);
  // const hours = toBanglaNum(getHour > 12 ? getHour - 12 : getHour);
  // const merridian = getHour > 12 ? "অপরাহ্ণ" : "পূর্বাহ্ণ";

  return `${date} ${monthname} ${year}, ${hours}:${minutes}`;
};

export const modifyHeaderPosition = (isDesktop, stickyHeader) => {
  const getHeader = document.getElementById("header");
  if (isDesktop) {
    stickyHeader
      ? (getHeader.style.cssText = "position:sticky; top: 0; z-index: 2")
      : (getHeader.style.cssText = "position:relative");
  }
};

export const getDomainSectionInfo = (domainSlug, sections) => {
  return sections.find(item => item["domain-slug"] === domainSlug && item["parent-id"] === null);
};

const sectionObjects = ({ parentSectionObj = {}, currentSectionObj = {}, childSections = [] }) => {
  const { "display-name": parentDisplayName, "section-url": parentSectionUrl } = parentSectionObj;

  const {
    "display-name": currentDisplayName,
    "section-url": currentSectionUrl,
    "parent-id": currentSectionParentId
  } = currentSectionObj;
  const requiredObj = {};

  requiredObj.parentSection = parentDisplayName || null;
  requiredObj.parentSectionUrl = parentSectionUrl || null;
  requiredObj.currentSection = currentDisplayName || null;
  requiredObj.parentSectionId = currentSectionParentId || null;
  requiredObj.currentSectionUrl = currentSectionUrl || null;
  requiredObj.childSections = childSections;

  return requiredObj;
};

export const getSectionsDataForHeader = (sections, domainSlug, currentSection = {}, domainSectionObj) => {
  const currentSectionObj = sections.find(item => item.id === currentSection[0].id);
  if (currentSectionObj["parent-id"]) {
    const parentSectionObj = sections.find(item => item.id === currentSectionObj["parent-id"]);
    const siblingSections = sections.filter(item => item["parent-id"] === parentSectionObj.id);

    let childSections = [];

    if (!domainSlug || domainSectionObj.id !== currentSectionObj["parent-id"]) {
      childSections = siblingSections;
    } else {
      childSections = sections.filter(item => item["parent-id"] === currentSectionObj.id);
    }

    return sectionObjects({ parentSectionObj, currentSectionObj, childSections });
  } else {
    const childSections = sections.filter(item => item["parent-id"] === currentSectionObj.id);
    return sectionObjects({ currentSectionObj, childSections });
  }
};

export const getBanglaDate = value => {
  const monthname = toBanglaMonth(getMonth(value) + 1);
  const date = toBanglaNum(getDate(value));
  const year = toBanglaNum(getYear(value));

  return `${date} ${monthname} ${year}`;
};

export const getEngDate = (value, type = "default") => {
  let monthName = getMonth(value) + 1;

  if (type === "monthname") {
    monthName = toEngMonth(monthName);
  }

  const date = getDate(value);
  const year = getYear(value);

  return `${date} ${monthName} ${year}`;
};

export const useOuterClickNotifier = (onOuterClick, innerRef) => {
  useEffect(
    () => {
      // only add listener, if the element exists
      if (innerRef.current) {
        document.addEventListener("click", handleClick);
      }

      // unmount previous first in case input has changed
      return () => document.removeEventListener("click", handleClick);

      function handleClick(e) {
        innerRef.current && !innerRef.current.contains(e.target) && onOuterClick(e);
      }
    },
    [onOuterClick, innerRef] // invoke again, if inputs has changed
  );
};

export const getKeyHighlights = cards => {
  return cards && cards.length > 0
    ? cards
        .filter(card => get(card, ["metadata", "attributes", "key-event"], false))
        .map(card => ({
          title: card["story-elements"].find(element => element.type === "title").text,
          card
        }))
    : null;
};

export const getAuthorName = story => {
  const authorDisplayName = get(story, ["metadata", "story-attributes", "author-display-name", "0"], null);
  const authorObj = get(story, ["authors", "0"], null);
  const authorName = (authorObj && authorObj.name) || story["author-name"];
  return authorDisplayName || authorName;
};

export const getExcerpt = (story, maxLength = 200) => {
  let excerpt = get(story, ["metadata", "excerpt"], get(story, ["metadata", "summary"]));

  if (excerpt && excerpt.length > maxLength) {
    const extractString = excerpt.substr(0, maxLength);
    excerpt = extractString.substr(0, Math.min(extractString.length, extractString.lastIndexOf(" "))).concat(" ...");
  }
  return excerpt;
};

export const getHeadline = story => {
  if (!story) {
    return null;
  }
  const alternateHeadline = get(story, ["alternative", "home", "default", "headline"], null);
  if (!alternateHeadline) {
    return story.headline;
  }
  return alternateHeadline;
};

// Validation rules for the password
export const validatePassword = str => {
  if (!str) {
    return "Please provide the Password";
  } else if (str.length < 8) {
    return "Password too short";
  } else if (str.search(/\d/) === -1) {
    return "Password needs to have atleast 1 Number";
  } else if (str.search(/[a-zA-Z]/) === -1) {
    return "Password needs to have atleast 1 letter";
  }

  return null; // Indicates password is valid
};

export const getSocialLinks = (domainName, socialLinks) => {
  switch (domainName) {
    case "protichinta": {
      return {
        fbUrl: "https://www.facebook.com/Protichinta",
        twitterUrl: "https://twitter.com/Protichinta",
        watsappUrl: "",
        messengerUrl: "",
        launcherUrl: ""
      };
    }
    case "biggan-chinta": {
      return {
        fbUrl: "https://www.facebook.com/bigganchinta/",
        twitterUrl: "https://twitter.com/bigganchinta",
        InstagramUrl: "/#",
        YouTubeUrl: "/#",
        watsappUrl: "",
        messengerUrl: "",
        launcherUrl: ""
      };
    }
    case "bigganchinta": {
      return {
        fbUrl: "https://www.facebook.com/bigganchinta/",
        twitterUrl: "https://twitter.com/bigganchinta",
        InstagramUrl: "/#",
        YouTubeUrl: "/#",
        watsappUrl: "",
        messengerUrl: "",
        launcherUrl: ""
      };
    }
    case "bondhushava": {
      return {
        fbUrl: "https://www.facebook.com/Bondhushava.PA/",
        twitterUrl: "https://twitter.com/PABondhushava",
        InstagramUrl: "/#",
        YouTubeUrl: "https://www.youtube.com/channel/UCQAUNRo5q4O5Y2Y1cEsOPYA",
        watsappUrl: "",
        messengerUrl: "",
        launcherUrl: ""
      };
    }
    case "kishoralo": {
      return {
        fbUrl: "https://www.facebook.com/kishor.alo",
        twitterUrl: "https://twitter.com/kishor_alo",
        InstagramUrl: "https://www.instagram.com/kishoralo/",
        YouTubeUrl: "https://www.youtube.com/channel/UC6t_CZ9pX9PlBxPRYiNM6vA",
        LinkedInUrl: "https://www.linkedin.com/company/kishor-alo/",
        PinterestUrl: "https://www.pinterest.com/kishoralo/",
        watsappUrl: "",
        messengerUrl: "",
        launcherUrl: ""
      };
    }
    case "trust": {
      return {
        fbUrl: "https://www.facebook.com/Palotrust",
        twitterUrl: "https://twitter.com/prothomalotrust",
        InstagramUrl: "/#",
        YouTubeUrl: "/#",
        watsappUrl: "",
        messengerUrl: "",
        launcherUrl: ""
      };
    }
    default: {
      return {
        fbUrl: socialLinks ? socialLinks["facebook-url"] : "",
        twitterUrl: socialLinks ? socialLinks["twitter-url"] : "",
        InstagramUrl: socialLinks ? socialLinks["instagram-url"] : "",
        YouTubeUrl: socialLinks ? socialLinks["youtube-url"] : "",
        LinkedInUrl: socialLinks ? socialLinks["linkedin-url"] : "",
        PinterestUrl: socialLinks ? socialLinks["pinterest-url"] : "",
        watsappUrl: socialLinks ? socialLinks["whatsapp-url"] : "",
        messengerUrl: socialLinks ? socialLinks["messenger-url"] : "",
        launcherUrl: socialLinks ? socialLinks["telegram-url"] : "",
        emailUrl: socialLinks ? socialLinks["email-url"] : ""
      };
    }
  }
};

export const getMultiDomainMenuData = (
  domainSlug,
  protichintaMenuItems,
  trustMenuItems,
  kishoraloMenuItems,
  bondhushavaMenuItems,
  bigganChintaMenuItems,
  prothomaloMenuItems,
  nagorikMenuItems
) => {
  switch (domainSlug) {
    case "protichinta":
      return protichintaMenuItems;
    case "trust":
      return trustMenuItems;
    case "kishoralo":
      return kishoraloMenuItems;
    case "bondhushava":
      return bondhushavaMenuItems;
    case "biggan-chinta":
      return bigganChintaMenuItems;
    case "bigganchinta":
      return bigganChintaMenuItems;
    case "nagorik-sangbad":
      return nagorikMenuItems;
    default:
      return prothomaloMenuItems;
  }
};
// Process the required response in appropriate format

export const processComments = data => {
  const list = [];

  data.forEach(item => {
    const headline = get(item, ["attributes", "page", "headline"], null);

    if (headline) {
      const optionList = get(item, ["attributes", "body", "ops"], []);
      const url = get(item, ["attributes", "page", "url"], "");
      const commentList = [];

      optionList.forEach(option => {
        if (typeof option.insert === "string") {
          commentList.push({
            message: option.insert,
            isMentioned: false
          });
        } else {
          if (option.insert && option.insert.mentions) {
            commentList.push({
              link: get(option, ["attributes", "link"], ""),
              isMentioned: true
            });
          }
        }
      });

      list.push({
        comments: commentList,
        createdAt: item.attributes.created_at,
        headline,
        url
      });
    }
  });

  return list;
};

export const headerLevelLogo = pageType => {
  switch (pageType) {
    case "home-page":
      return 1;
    case "story-page":
      return 2;
    case "section-page":
      return 2;
    case "notification-page":
      return 2;
    case "tag-page":
      return 2;
    case "author-page":
      return 2;
    case "collection-page":
      return 2;
    case "static-page":
      return 2;
    case "search-page":
      return 2;
    default:
      return 1;
  }
};

export const appDownloadLinks = (lang, platform) => {
  if (lang === "en") {
    return platform === "google" ? ENG_GOOGLE_PLAYSTORE : ENG_APPLE_PLAYSTORE;
  } else {
    return platform === "google" ? BN_GOOGLE_PLAYSTORE : BN_APPLE_PLAYSTORE;
  }
};

export const getCurrentSite = currentPath => {
  if (currentPath) {
    if (currentPath.includes("kia") || currentPath.includes("kishoralo")) {
      return "kishoralo";
    } else if (currentPath.includes("bs") || currentPath.includes("bondhushava")) {
      return "bondhushava";
    } else if (currentPath.includes("pc") || currentPath.includes("protichinta")) {
      return "protichinta";
    } else if (currentPath.includes("bc") || currentPath.includes("bigganchinta")) {
      return "bigganchinta";
    } else if (currentPath.includes("trust")) {
      return "trust";
    } else if (currentPath.includes("nagorik")) {
      return "nagorik";
    } else if (
      currentPath.includes("en-uat.prothomalo.qtstage.io") ||
      currentPath.includes("en.prothomalo.qtstage.io") ||
      currentPath.includes("en-beta.prothomalo.com") ||
      currentPath.includes("en.prothomalo.com")
    ) {
      return "en";
    } else {
      return "bn";
    }
  } else return "bn";
};

export const getHeaderUrl = currentPath => {
  const params = parseUrl(currentPath);
  const redirectUrl = get(params, ["query", "redirect-url"], "https://www.prothomalo.com");
  return redirectUrl;
};

export const bgColor = domainSlug => {
  switch (domainSlug) {
    case "kishoralo":
      return "#eee";
    case "protichinta":
      return "#fbf6de";
    default:
      return "#eee";
  }
};

export const fontColor = domainSlug => {
  switch (domainSlug) {
    case "kishoralo":
      return "#121212";
    case "protichinta":
      return "#222";
    default:
      return "#222";
  }
};

export const fontVariation = (type, elements, customGalleryElements, lang) => {
  const divisableNumber = lang === "en" ? 8 : 10;
  const incrementer = lang === "en" ? 0.25 : 0.2;

  const processFontStyle = (type, element) => {
    return (element.style.fontSize =
      type === "add"
        ? parseFloat(getComputedStyle(element).fontSize) / divisableNumber + incrementer + "rem"
        : parseFloat(getComputedStyle(element).fontSize) / divisableNumber - incrementer + "rem");
  };

  const computeStyles = elements => {
    Array.from(elements).forEach(element => processFontStyle(type, element));
  };

  computeStyles(elements);
  computeStyles(customGalleryElements);
};
